import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ChatBubbleBottomCenterTextIcon, XCircleIcon, ArrowPathIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';

interface AddRemarkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (remark: string) => Promise<void>;
  taskName: string;
  mode?: 'remark' | 'rejection' | 'reset' | 'returned';
}

export default function AddRemarkModal({
  isOpen,
  onClose,
  onSubmit,
  taskName,
  mode = 'remark'
}: AddRemarkModalProps) {
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!remark.trim()) return;

    setSubmitting(true);
    try {
      await onSubmit(remark.trim());
      setRemark('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const getConfig = () => {
    switch (mode) {
      case 'rejection':
        return {
          title: 'Reject Task',
          icon: XCircleIcon,
          iconColor: 'text-red-600',
          label: 'Rejection Reason *',
          placeholder: 'Please provide a reason for rejecting this task...',
          submitText: 'Reject',
          submittingText: 'Rejecting...',
          submitButtonClass: 'flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors'
        };
      case 'reset':
        return {
          title: 'Reset Status',
          icon: ArrowPathIcon,
          iconColor: 'text-orange-600',
          label: 'Reset Reason *',
          placeholder: 'E.g., Lost access card and need replacement, need to redo the flow...',
          submitText: 'Reset Status',
          submittingText: 'Resetting...',
          submitButtonClass: 'flex-1 px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors'
        };
      case 'returned':
        return {
          title: 'Mark as Returned',
          icon: ArrowUturnLeftIcon,
          iconColor: 'text-teal-600',
          label: 'Return Remarks *',
          placeholder: 'E.g., Company car returned on [date], returned in good condition...',
          submitText: 'Mark Returned',
          submittingText: 'Updating...',
          submitButtonClass: 'flex-1 px-4 py-2 text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors'
        };
      default:
        return {
          title: 'Add Remark',
          icon: ChatBubbleBottomCenterTextIcon,
          iconColor: 'text-blue-600',
          label: 'Remark *',
          placeholder: 'Add notes or comments about this task...',
          submitText: 'Add Remark',
          submittingText: 'Adding...',
          submitButtonClass: 'flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors'
        };
    }
  };

  const config = getConfig();

  const IconComponent = config.icon;

  return createPortal(
    <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <IconComponent className={`h-6 w-6 ${config.iconColor} mr-2`} />
            <h3 className="text-lg font-semibold text-gray-900">{config.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">Task: {taskName}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="remark" className="block text-sm font-medium text-gray-700 mb-1">
              {config.label}
            </label>
            <textarea
              id="remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={config.placeholder}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !remark.trim()}
              className={config.submitButtonClass}
            >
              {submitting ? config.submittingText : config.submitText}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
